"use client";

/**
 * AgentsFilterBar — group filter chip row for /agents directory index.
 *
 * Client island: reads ?group= from URL via useSearchParams().
 * On group change, sets data-active-group on #pseo-agents-grid-wrapper
 * so CSS can hide non-matching cards without removing them from the DOM
 * (keeps all 23 cards crawlable by Google).
 *
 * Must be wrapped in <Suspense> by the server parent (required by Next.js
 * when using useSearchParams inside a static export).
 */

import { useEffect } from "react";
import { useSearchParams } from "next/navigation";

type AgentGroupKey = "research" | "writing" | "code" | "operations" | "specialist";

interface Props {
  groups: AgentGroupKey[];
  groupLabels: Record<AgentGroupKey, string>;
  groupCounts: Record<AgentGroupKey, number>;
  totalCount: number;
}

export default function AgentsFilterBar({
  groups,
  groupLabels,
  groupCounts,
  totalCount,
}: Props) {
  const searchParams = useSearchParams();
  const activeGroup = searchParams.get("group") as AgentGroupKey | null;

  // Toggle data-active-group on the grid wrapper to drive CSS visibility
  useEffect(() => {
    const wrapper = document.getElementById("pseo-agents-grid-wrapper");
    if (!wrapper) return;

    if (activeGroup && groups.includes(activeGroup)) {
      wrapper.setAttribute("data-active-group", activeGroup);
    } else {
      wrapper.removeAttribute("data-active-group");
    }
  }, [activeGroup, groups]);

  return (
    <nav aria-label="Filter agents by group" className="pseo-filter-chip-row">
      {/* All chip */}
      <a
        href="/agents"
        className="pseo-filter-chip"
        data-active={!activeGroup ? "true" : "false"}
      >
        All <span className="pseo-filter-chip-count">{totalCount}</span>
      </a>

      {/* Group chips */}
      {groups.map((g) => (
        <a
          key={g}
          href={`/agents?group=${g}`}
          className="pseo-filter-chip"
          data-active={activeGroup === g ? "true" : "false"}
        >
          {groupLabels[g]}{" "}
          <span className="pseo-filter-chip-count">{groupCounts[g]}</span>
        </a>
      ))}
    </nav>
  );
}
